import { Injectable } from '@angular/core';
import { CastingTypeFormat } from 'src/app/components/att-casting-view/formats/casting-type-formats-sel/casting-type-formats-sel.component';
import { NegativeNumberFormatting } from 'src/app/components/att-casting-view/formats/negative-numbers-sel/negative-numbers-sel.component';
import { CastingCurrencyValue, CastingMode, CastingValue } from 'src/app/components/att-casting-view/att-casting-view.component';
import { Qualifier } from 'src/app/types/qualifier';
import { AttributeInfo } from 'src/app/types/@types/sl-v-types';
import { pad } from 'lodash';
import { NumberSciQualifierService } from './number-sci-qualifier.service';
import { NumberFractionQualifierService } from './number-fraction-qualifier.service';

export type CurrencyCode = 'USD' | 'EUR' | 'JPY' | 'GBP' | 'AUD' | 'INR' | 'BYR' | 'CNY' | 'CAD';
export type CurrencyLUT = {
  [key: string]: Currency;
}

export type Currency = {
  name: string;
  code: CurrencyCode;
  symbol: string;
  char: number;
}

@Injectable({
  providedIn: 'root'
})
export class NumberQualifierService implements Qualifier {
  static supportedStats = ['avg', 'count', 'first', 'last', 'max', 'median', 'min', 'sum', 'stddev'];
  public static currencies: Array<Currency> = [
    { name: 'U.S. Dollars', code: 'USD', symbol: '$', char: 0x24 },
    { name: 'Euro', code: 'EUR', symbol: '&euro;', char: 0x20ac },
    { name: 'Japanese Yen', code: 'JPY', symbol: '&yen;', char: 0xa5 },
    { name: 'Great British Pound', code: 'GBP', symbol: '&pound;', char: 0xa3 },
    { name: 'Australian Dollar', code: 'AUD', symbol: '$', char: 0x24 },
    { name: 'Rupee', code: 'INR', symbol: '&#8377;', char: 0x20b9 },
    { name: 'Ruble', code: 'BYR', symbol: '&#8381;', char: 0x20bd },
    { name: 'Yuan', code: 'CNY', symbol: '&#20803;', char: 0xa5 },
    { name: 'Canadian Dollar', code: 'CAD', symbol: '$', char: 0xa5 }
  ]
  public static CurrencyLUT: CurrencyLUT = {
    USD: NumberQualifierService.currencies[0],
    EUR: NumberQualifierService.currencies[1],
    JPY: NumberQualifierService.currencies[2],
    GPB: NumberQualifierService.currencies[3],
    AUD: NumberQualifierService.currencies[4],
    INR: NumberQualifierService.currencies[5],
    BYR: NumberQualifierService.currencies[6],
    CNY: NumberQualifierService.currencies[7],
    CAD: NumberQualifierService.currencies[8]
  }

  static ToString(value: number, format: CastingTypeFormat = 'as-is', fixed?: number, negFormat?: NegativeNumberFormatting,
    currency?: CastingCurrencyValue): string {
    if ([undefined, null, '', NaN].indexOf(value) > -1) {
      return '';
    }
    if (isNaN(Number(value))) {
      return '';
    }
    let decSep = '';
    let groupSep = '';
    let sciMode = '';
    let useSci = false;
    switch (format) {
      case 'as-is':
        groupSep = '';
        decSep = '.';
        break;
      case 'dot-comma':
        groupSep = '.';
        decSep = ',';
        break;
      default:
      case 'comma-dot':
        groupSep = ',';
        decSep = '.';
        break;
      case 'space-comma':
        groupSep = ' ';
        decSep = ',';
        break;
      case 'space-dot':
        groupSep = ' ';
        decSep = '.';
        break;
      case 'sci':
        sciMode = 'E';
        groupSep = '';
        decSep = '.';
        useSci = true;
        break;
      case 'sci-eng':
        sciMode = 'E';
        groupSep = '';
        decSep = '.';
        useSci = true;
        break;
      case 'sci-pow':
        sciMode = 'pow';
        groupSep = '';
        decSep = '.';
        useSci = true;
        break;

    }

    let useParen = false;
    let showNeg = true;
    if (!!negFormat) {
      switch (negFormat.format) {
        case 'neg':
          useParen = false;
          showNeg = true;
          break;
        case 'neg-paren':
          useParen = true;
          showNeg = true;
          break;
        case 'paren':
          useParen = true;
          showNeg = false;
          break;
      }
    }

    let valString = '';
    const absValue: number = Math.abs(value);
    if (useSci === true) {
      valString = NumberSciQualifierService.ToString(value, format, fixed, true);
      /*
      valString = absValue.toExponential(fixed).trim();
      switch (sciMode) {
        case 'pow':
          valString = '' + absValue.toExponential(fixed).replace(/[eE](.*)/, ' x 10<sup>$1</sup>').replace('+', '').trim();
          break;
      }*/

    } else if (!!fixed) {
      const fixedVal: string = absValue.toFixed(fixed);
      const fixedParts = fixedVal.split('.');
      valString = NumberQualifierService.GroupDigits(Number(fixedParts[0]), groupSep);
      if (fixedParts.length > 1) {
        if (fixedParts[1].length > 0) {
          valString += decSep + fixedParts.pop();
        }
      }
    } else {
      valString = NumberQualifierService.GroupDigits(Math.abs(value), groupSep);
    }
    let result = '';
    let needClosingSpan = false;

    if (value < 0) {
      if (negFormat.color !== '#000000') {
        result += '<span style="color:' + negFormat.color + '">';
        needClosingSpan = true;
      }
    }
    let useCurrCode = false;
    let useCurrSymbol = false;
    let symbolStart = false;
    let codeStart = false;
    let useStartSpace = false;
    let currencyInfo: Currency;
    if (!!currency) {
      currencyInfo = NumberQualifierService.CurrencyLUT[currency.code];
      switch (currency.format) {
        case '#C':
          useCurrCode = true;
          codeStart = false;
          break;
        case 'C#':
          useCurrCode = true;
          codeStart = true;
          break;
        case 'S#':
          useCurrSymbol = true;
          symbolStart = true;
          break;
        case 'S#C':
          useCurrCode = true;
          codeStart = false;
          useCurrSymbol = true;
          symbolStart = true;
          break;
        case 'Ssp#':
          useCurrSymbol = true;
          useStartSpace = true;
          useCurrCode = false;
          symbolStart = true;
          break;
        case 'Ssp#C':
          useCurrSymbol = true;
          useStartSpace = true;
          useCurrCode = true;
          symbolStart = true;
          break;
      }
    }

    if (value < 0) {

      if (showNeg === true) {
        result += '-';
      }
      if (useParen === true) {
        result += '(';
      }
      if (symbolStart === true) {
        if (useCurrSymbol === true) {
          result += currencyInfo.symbol;
          if (useStartSpace === true) {
            result += ' ';
          }

        }
      }
      if (codeStart === true) {
        if (useCurrCode === true) {
          if (useStartSpace === true) {
            result += ' ';
          }
          result += currencyInfo.code + ' ';
        }
      }
      result += valString;

      if (codeStart === false) {
        if (useCurrCode === true) {
          result += ' ' + currencyInfo.code;
        }
      }
      if (useParen === true) {
        result += ')';
      }


    } else {
      if (symbolStart === true) {
        if (useCurrSymbol === true) {
          result += currencyInfo.symbol;
          if (useStartSpace === true) {
            result += ' ';
          }
        }
      }
      if (codeStart === true) {
        if (useCurrCode === true) {
          result += currencyInfo.code + ' ';
        }
      }
      result += valString;
      if (codeStart === false) {
        if (useCurrCode === true) {
          result += ' ' + currencyInfo.code;
        }
      }

    }
    if (needClosingSpan === true) {
      result += '</span>';
    }
    return result;

  }
  static FromString(value: string, format: CastingTypeFormat = 'dot-comma',
    currency?: CastingCurrencyValue): number {
    if (format === 'as-is') {
      return Number(value);
    }
    let decSep = '';
    let groupSep = '';
    let useSci = false;
    let sciMode = '';
    switch (format) {
      case 'dot-comma':
        groupSep = '.';
        decSep = ',';
        break;
      default:
      case 'comma-dot':
        groupSep = ',';
        decSep = '.';
        break;
      case 'space-comma':
        groupSep = ' ';
        decSep = ',';
        break;
      case 'space-dot':
        groupSep = ' ';
        decSep = '.';
        break;
      case 'sci':
        sciMode = 'E';
        groupSep = '';
        decSep = '.';
        useSci = true;
        break;
      case 'sci-pow':
        sciMode = 'pow';
        groupSep = '';
        decSep = '.';
        useSci = true;
        break;
    }

    let targetValue = value;
    if (value.toLowerCase().indexOf('<span') > -1) {
      targetValue = value.split(/>(.+)\</)[1]; // gets stuff in span tag
    }
    const hasNeg = targetValue.indexOf('-') > -1;
    const hasParen = targetValue.indexOf('(') > -1;
    const isNegative = hasNeg || hasParen;

    if (hasParen === true) {
      const parenned = targetValue.split(/\((.+)\)/)
      for (const item of parenned) {
        if (['', '-'].indexOf(item.trim()) > -1) {
          continue;
        }
        targetValue = item;
        break;
      }
    }
    if (!!currency) {
      const currencyInfo: Currency = NumberQualifierService.CurrencyLUT[currency.code];
      targetValue = targetValue.replace(currencyInfo.code, '');
      targetValue = targetValue.replace(currencyInfo.symbol, '');
    }
    if (useSci === true) {
      switch (sciMode) {
        case 'sci':
          return Number(targetValue);
        case 'pow':
          return Number(targetValue.replace(/ x 10\<sup\>([+-]*\d+)\<\/sup\>?/, 'e$1'));
      }
    }
    targetValue = targetValue.trim();
    const parts = targetValue.split(decSep);
    parts[0] = parts[0].split(groupSep).join('');
    let numberVal = Number(parts.join('.'));
    if (numberVal > 0) {
      if (isNegative === true) {
        numberVal = -numberVal;
      }
    }
    return numberVal;
  }

  public static GroupDigits(val: number, groupSep: string) {
    return String(val).replace(/(.)(?=(\d{3})+$)/g, '$1' + groupSep)
  }
  public static GetDisplayValue(attribute: AttributeInfo, castingMode: CastingMode, val: any): string {
    if (!attribute.casting) {
      return val;
    }
    if (!attribute.casting[castingMode]) {
      return val;
    }
    const casting: CastingValue = attribute.casting[castingMode] as CastingValue;
    switch (attribute.type) {
      case 'text':
      case 'string':
        val = Number(val);
    }

    let digits: number;
    if (!!casting.fixedDigits || !!casting.precision) {
      if (!!casting.fixedDigits) {
        digits = casting.fixedDigits;
      } else {
        digits = casting.precision;
      }
    }
    return NumberQualifierService.ToString(val, casting.format, digits, casting.negFormat, casting.currency);
  }
  public static GetTextExportValue(attribute: AttributeInfo, castingMode: CastingMode, val: any): string {
    if ([undefined, null].indexOf(val) > -1) {
      return '';
    }
    return NumberQualifierService.GetDisplayValue(attribute, castingMode, val);
  }
  public static GetNativeValue(attribute: AttributeInfo, castingMode: CastingMode, value: any): any {
    const casting: CastingValue = attribute.casting[castingMode];
    return NumberQualifierService.FromString(value, casting.format, casting.currency);
  }
  public static SupportsStat(statName): boolean {
    return NumberQualifierService.supportedStats.indexOf(statName) > -1;
  }
  constructor() { }
  public static GetNumFormat(attribute: AttributeInfo, castingMode: CastingMode) {
    let casting: CastingValue = null;
    if (!!attribute.casting && !!attribute.casting[castingMode]) {
      casting = attribute.casting[castingMode];
    } else if (!!attribute.casting && !!attribute.casting.entry) {
      casting = attribute.casting.entry;
    } else {
      return '@';
    }
    const format = casting.format;
    let fixed = casting.fixedDigits;
    const negFormat: NegativeNumberFormatting = casting.negFormat;
    const currency: CastingCurrencyValue = casting.currency;
    let currencyStart = '';
    let currencyEnd = '';
    let baseFormat = '';

    if (!!currency) {
      //C = Code
      //S = Symbol
      //sp = space
      const symbol = String.fromCharCode(NumberQualifierService.CurrencyLUT[currency.code].char);
      const code = NumberQualifierService.CurrencyLUT[currency.code].code;
      switch (currency.format) {
        case '#C':
          currencyEnd = ` "${code}"`;
          break;
        case 'C#':
          currencyStart = ` "${code}"`;
          break;
        case 'S#':
          currencyStart = symbol;
          break;
        case 'S#C':
          currencyStart = symbol;
          currencyEnd = ` "${code}"`;
          break;
        case 'Ssp#':
          currencyStart = symbol + ' ';
          break;
        case 'Ssp#C':
          currencyStart = symbol + ' ';
          currencyEnd = ` "${code}"`;
          break;
      }
    }
    switch (format) {
      case 'sci':
      case 'sci-pow':
        if (!fixed) {
          fixed = casting.precision;
        }
        if (!fixed) {
          fixed = 2;
        }
        baseFormat = '0.' + (''.padEnd(fixed, '0')) + 'E+00';
        break;
      case 'sci-eng':
        if (!fixed) {
          fixed = casting.precision;
        }
        if (!fixed) {
          fixed = 2;
        }
        baseFormat = '###.' + (''.padEnd(fixed, '0')) + 'E+00';
        break;
      case 'dot-comma':
      case 'comma-dot':
      case 'space-comma':
      case 'space-dot':
      default:
        baseFormat = '#,0';
        if (!!fixed) {
          baseFormat += '.' + (''.padEnd(fixed, '0'));
        }
        break;

    }
    let fractional = '';
    if (casting.fractional) {
      fractional = NumberFractionQualifierService.GetNumFormat(attribute, castingMode);
      baseFormat = '#,0';
    }
    let negFmtStart = '';
    let negFmtEnd = '';
    let negColor = '';
    if (!!negFormat) {

      if (negFormat.color !== '#000000') {
        negColor = `[${negFormat.color}]`;
      }
      switch (negFormat.format) {
        case 'neg':
          negFmtStart = '-';
          break;
        case 'neg-paren':
          negFmtStart = '(-';
          negFmtEnd = ')';
          break;
        case 'paren':
          negFmtStart = '(';
          negFmtEnd = ')';
          break;
      }
    }
    let fmt = `${currencyStart}${baseFormat}${fractional}${currencyEnd}`;
    if (!!negFormat) {
      fmt += `;${negColor}${currencyStart}${negFmtStart}${baseFormat}${fractional}${currencyEnd}${negFmtEnd}`;
    }
    return fmt;

  }
  public static GetStNdRd(value: number) {
    switch (value) {
      case 1:
        return 'first';
      case 2:
        return 'second';
      case 3:
        return 'third';
    }
    let out = '' + value;
    const last = out.substring(out.length-1,out.length);
    switch (last) {
      case '1':
        out += 'st';
        break;
      case '2':
        out += 'nd';
        break;
      case '3':
        out += 'rd';
        break;
      default:
        out+= 'th';
        break;
    }
    return out;
  }
}
